import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { 
  ipoService, 
  gmpService, 
  newsService, 
  jobService, 
  referralService, 
  affiliateService, 
  statsService,
  profileService,
  learningService 
} from "@/lib/dataService";
import { 
  IPO, 
  IpoGmpHistory, 
  News, 
  Job, 
  Referral, 
  AffiliateLink, 
  DashboardStats, 
  UserProfile,
  Learning,
  MarketTicker,
  IPO_Legacy,
  NewsItem_Legacy,
  Job_Legacy,
  AffiliateLink_Legacy,
  Learning_Legacy,
  MarketTicker_Legacy
} from "@/types/models";
import { authService } from "@/lib/authService";
import { getGmpPercentage } from "@/lib/gmpUtils";
import { mockTickerService } from "@/lib/mockTickerService";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { toApiDate } from "@/lib/dateFormat";
import { isUsdInrTicker } from "@/lib/tickerDirection";
import { sanitizeRichTextHtml } from "@/lib/htmlSanitizer";

// Legacy interface for backward compatibility
export interface GmpHistoryEntry {
  date: string;
  gmp: number;
}

type GmpHistoryRow = {
  date: string;
  gmp_value: number | string;
};

interface DataContextType {
  // Data arrays (legacy format for compatibility)
  ipos: IPO_Legacy[];
  news: NewsItem_Legacy[];
  jobs: Job_Legacy[];
  learning: Learning_Legacy[];
  affiliates: AffiliateLink_Legacy[];
  marketTickers: MarketTicker_Legacy[];
  gmpHistory: Record<string, GmpHistoryEntry[]>;
  newsCategories: string[];
  jobCategories: string[];
  jobLocations: string[];

  // CRUD operations
  addIpo: (ipo: IPO_Legacy) => void;
  updateIpo: (id: string, ipo: Partial<IPO_Legacy>) => void;
  deleteIpo: (id: string) => void;
  addNews: (item: NewsItem_Legacy) => void;
  updateNews: (id: string, item: Partial<NewsItem_Legacy>) => void;
  deleteNews: (id: string) => void;
  addJob: (job: Job_Legacy) => void;
  updateJob: (id: string, job: Partial<Job_Legacy>) => void;
  deleteJob: (id: string) => void;
  addLearning: (learning: Learning_Legacy) => void;
  updateLearning: (id: string, learning: Partial<Learning_Legacy>) => void;
  deleteLearning: (id: string) => void;
  addAffiliate: (link: AffiliateLink_Legacy) => void;
  updateAffiliate: (id: string, link: Partial<AffiliateLink_Legacy>) => void;
  deleteAffiliate: (id: string) => void;
  addMarketTicker: (ticker: MarketTicker_Legacy) => void;
  updateMarketTicker: (id: string, ticker: Partial<MarketTicker_Legacy>) => void;
  deleteMarketTicker: (id: string) => void;
  addGmpEntry: (ipoId: string, entry: GmpHistoryEntry) => void;

  // Admin authentication
  isAdmin: boolean;
  adminLogin: (email: string, password: string) => Promise<boolean>;
  adminLogout: () => Promise<void>;

  // Loading states
  loading: boolean;
  authLoading: boolean;
  error: string | null;
}

const DataContext = createContext<DataContextType>({} as DataContextType);

// Helper functions to convert between legacy and new formats
const convertIpoToLegacy = (ipo: IPO): IPO_Legacy => ({
  id: ipo.id,
  company: ipo.company_name,
  issueSize: ipo.issue_size,
  priceBand: ipo.price_band,
  openDate: ipo.open_date,
  closeDate: ipo.close_date,
  listingDate: ipo.listing_date || "",
  allotmentDate: ipo.allotment_date || "",
  status: ipo.status,
  subscriptionStatus: ipo.subscription_status || "",
  gmp: 0, // Will be populated from GMP history
  yesterdayGmp: 0, // Will be populated from GMP history
  estimatedListing: ipo.estimated_listing || "",
  lotSize: ipo.lot_size || 0,
  exchange: ipo.exchange || "",
});

const convertLegacyToIpo = (ipo: IPO_Legacy): Partial<IPO> => ({
  company_name: ipo.company,
  issue_size: ipo.issueSize,
  price_band: ipo.priceBand,
  open_date: ipo.openDate,
  close_date: ipo.closeDate,
  listing_date: ipo.listingDate || null,
  allotment_date: ipo.allotmentDate || null,
  status: ipo.status,
  subscription_status: ipo.subscriptionStatus || null,
  exchange: ipo.exchange || null,
  lot_size: ipo.lotSize || null,
  estimated_listing: ipo.estimatedListing || null,
});

const convertNewsToLegacy = (news: News): NewsItem_Legacy => ({
  id: news.id,
  title: news.title,
  summary: news.summary,
  content: news.content || "",
  contentAlignment: news.content_alignment || "justify",
  fontStyle: news.font_style || "times_new_roman",
  category: news.category,
  date: news.publish_date || news.created_at || "",
  image: resolveMediaUrl(news.image_url),
  videoUrl: "", // News doesn't have video_url in database, could add this field later
  author: news.author,
  slug: news.slug, // Add slug field for SEO-friendly URLs
});

const convertLegacyToNews = (news: NewsItem_Legacy): Partial<News> => ({
  title: news.title,
  summary: sanitizeRichTextHtml(news.summary),
  content: sanitizeRichTextHtml(news.content || ""),
  content_alignment: news.contentAlignment || "justify",
  font_style: news.fontStyle || "times_new_roman",
  category: news.category,
  publish_date: toApiDate(news.date),
  image_url: news.image || null,
  author: news.author,
  status: "published",
});

const convertJobToLegacy = (job: Job): Job_Legacy => ({
  id: job.id,
  title: job.title,
  company: job.company,
  location: job.location,
  category: job.category,
  type: job.employment_type,
  salary: job.salary_range,
  postedDate: job.posted_date || job.created_at || "",
  applyLink: job.apply_link,
  description: job.description || "",
  contentAlignment: job.content_alignment || "justify",
  fontStyle: job.font_style || "times_new_roman",
  imageUrl: resolveMediaUrl(job.image_url),
  videoUrl: job.video_url,
  slug: job.slug,
});

const convertLegacyToJob = (job: Job_Legacy): Partial<Job> => ({
  title: job.title,
  company: job.company,
  location: job.location,
  category: job.category,
  employment_type: job.type as "Full-time" | "Part-time" | "Contract" | "Internship" | "Remote",
  salary_range: job.salary,
  posted_date: toApiDate(job.postedDate),
  apply_link: job.applyLink,
  description: sanitizeRichTextHtml(job.description || ""),
  content_alignment: job.contentAlignment || "justify",
  font_style: job.fontStyle || "times_new_roman",
  image_url: job.imageUrl,
  video_url: job.videoUrl,
  status: "active",
});

const convertAffiliateToLegacy = (affiliate: AffiliateLink): AffiliateLink_Legacy => ({
  id: affiliate.id,
  text: affiliate.title,
  url: affiliate.target_url,
  type: affiliate.type,
  imageUrl: resolveMediaUrl(affiliate.image_url),
  showOnPages: affiliate.show_on_pages || [],
});

const convertLegacyToAffiliate = (affiliate: AffiliateLink_Legacy): Partial<AffiliateLink> => ({
  title: affiliate.text,
  target_url: affiliate.url,
  type: affiliate.type,
  image_url: affiliate.imageUrl,
  show_on_pages: affiliate.showOnPages,
  status: "active",
});

const convertMarketTickerToLegacy = (ticker: any): MarketTicker_Legacy => ({
  id: ticker.id,
  symbol: ticker.symbol,
  name: ticker.name,
  currency:
    isUsdInrTicker({ symbol: ticker.symbol, name: ticker.name })
      ? "INR"
      : String(ticker.currency || "").toUpperCase() === "INR"
      ? "INR"
      : String(ticker.currency || "").toUpperCase() === "NONE"
      ? "NONE"
      : "USD",
  price: ticker.price,
  change: ticker.change,
  changePercent: ticker.changePercent,
  lastUpdated: ticker.lastUpdated,
});

const convertLegacyToMarketTicker = (ticker: MarketTicker_Legacy): any => ({
  id: ticker.id,
  symbol: ticker.symbol,
  name: ticker.name,
  currency:
    isUsdInrTicker({ symbol: ticker.symbol, name: ticker.name })
      ? "INR"
      : String(ticker.currency || "").toUpperCase() === "INR"
      ? "INR"
      : String(ticker.currency || "").toUpperCase() === "NONE"
      ? "NONE"
      : "USD",
  price: ticker.price,
  change: ticker.change,
  changePercent: ticker.changePercent,
  lastUpdated: ticker.lastUpdated,
  status: "active",
});

// Learning conversion functions
const convertLearningToLegacy = (learning: Learning): Learning_Legacy => ({
  id: learning.id,
  title: learning.title,
  description: learning.description || "",
  contentAlignment: learning.content_alignment || "justify",
  fontStyle: learning.font_style || "times_new_roman",
  category: learning.category,
  difficultyLevel: learning.difficulty_level,
  duration: learning.duration || "",
  imageUrl: resolveMediaUrl(learning.image_url),
  videoUrl: learning.video_url,
  resourceUrl: learning.resource_url,
  instructor: learning.instructor || "",
  status: learning.status,
  slug: learning.slug,
});

const convertLegacyToLearning = (learning: Learning_Legacy): Partial<Learning> => ({
  title: learning.title,
  description: sanitizeRichTextHtml(learning.description || ""),
  content_alignment: learning.contentAlignment || "justify",
  font_style: learning.fontStyle || "times_new_roman",
  category: learning.category,
  difficulty_level: learning.difficultyLevel as "beginner" | "intermediate" | "advanced",
  duration: learning.duration,
  image_url: learning.imageUrl,
  video_url: learning.videoUrl,
  resource_url: learning.resourceUrl,
  instructor: learning.instructor,
  status: learning.status as "draft" | "published" | "archived",
});

const normalizeGmpValue = (value: unknown): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const mapGmpRowsToEntries = (rows: GmpHistoryRow[] = []): GmpHistoryEntry[] =>
  rows
    .map((row) => ({
      date: row.date,
      gmp: normalizeGmpValue(row.gmp_value),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

const upsertGmpEntry = (entries: GmpHistoryEntry[], entry: GmpHistoryEntry): GmpHistoryEntry[] => {
  const index = entries.findIndex((item) => item.date === entry.date);
  if (index >= 0) {
    const updated = [...entries];
    updated[index] = entry;
    return updated.sort((a, b) => a.date.localeCompare(b.date));
  }

  return [...entries, entry].sort((a, b) => a.date.localeCompare(b.date));
};

const getGmpSnapshot = (entries: GmpHistoryEntry[]) => {
  if (!entries.length) {
    return { currentGmp: 0, yesterdayGmp: 0 };
  }

  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted[sorted.length - 1];
  const previous = sorted.length > 1 ? sorted[sorted.length - 2] : null;

  return {
    currentGmp: latest?.gmp ?? 0,
    yesterdayGmp: previous?.gmp ?? 0,
  };
};

export const DataProvider = ({ children }: { children: ReactNode }) => {
  const [ipos, setIpos] = useState<IPO_Legacy[]>([]);
  const [news, setNews] = useState<NewsItem_Legacy[]>([]);
  const [jobs, setJobs] = useState<Job_Legacy[]>([]);
  const [learning, setLearning] = useState<Learning_Legacy[]>([]);
  const [affiliates, setAffiliates] = useState<AffiliateLink_Legacy[]>([]);
  const [marketTickers, setMarketTickers] = useState<MarketTicker_Legacy[]>([]);
  const [gmpHistory, setGmpHistory] = useState<Record<string, GmpHistoryEntry[]>>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Category/filter lists
  const newsCategories = ["All", "Markets", "Economy", "IPO", "Commodities", "Startups", "Corporate"];
  const jobCategories = Array.from(new Set(["All", ...jobs.map((job) => job.category).filter(Boolean)]));
  const jobLocations = ["All", "Mumbai", "Bangalore", "Delhi", "Hyderabad", "Remote"];

  // Load initial data
  useEffect(() => {
    loadData();
    checkAuthStatus();
  }, []);

  // Load GMP data after IPOs are loaded (only once)
  useEffect(() => {
    if (ipos.length > 0 && !loading) {
      console.log("IPOs loaded, starting GMP data load...");
      loadGmpData();
    }
  }, [ipos.length, loading]); // Trigger when IPO count changes and loading is false

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Starting to load data from API...");

      // Load all data in parallel for better performance
      const loadPromises = [
        // Load IPOs
        (async () => {
          try {
            const iposData = await ipoService.getAll();
            console.log("IPOs loaded:", iposData);
            const legacyIpos = iposData.map(convertIpoToLegacy);
            setIpos(legacyIpos);
          } catch (err) {
            console.error("Error loading IPOs:", err);
            setIpos([]);
          }
        })(),

        // Load News
        (async () => {
          try {
            const newsData = await newsService.getAll();
            console.log("News loaded:", newsData);
            const legacyNews = newsData.map(convertNewsToLegacy);
            setNews(legacyNews);
          } catch (err) {
            console.error("Error loading news:", err);
            // Fallback to mock data on error only
            console.log("Error loading news from database, using mock data");
            const { newsData: mockNews } = await import("@/data/mockData");
            setNews(mockNews);
          }
        })(),

        // Load Jobs
        (async () => {
          try {
            const jobsData = await jobService.getAll();
            console.log("Jobs loaded:", jobsData);
            const legacyJobs = jobsData.map(convertJobToLegacy);
            setJobs(legacyJobs);
          } catch (err) {
            console.error("Error loading jobs:", err);
            // Fallback to mock data on error only
            console.log("Error loading jobs from database, using mock data");
            const { jobsData: mockJobs } = await import("@/data/mockData");
            setJobs(mockJobs);
          }
        })(),

        // Load Learning
        (async () => {
          try {
            const learningData = await learningService.getAll();
            console.log("Learning loaded:", learningData);
            const legacyLearning = learningData.map(convertLearningToLegacy);
            setLearning(legacyLearning);
          } catch (err) {
            console.error("Error loading learning:", err);
            // Fallback to empty array on error only
            console.log("Error loading learning from database, using empty array");
            setLearning([]);
          }
        })(),

        // Load Affiliates
        (async () => {
          try {
            const affiliatesData = await affiliateService.getAll();
            console.log("Affiliates loaded:", affiliatesData);
            const legacyAffiliates = affiliatesData.map(convertAffiliateToLegacy);
            setAffiliates(legacyAffiliates);
          } catch (err) {
            console.error("Error loading affiliates:", err);
            setAffiliates([]);
          }
        })(),

        // Load Market Tickers
        (async () => {
          try {
            const marketTickersData = await mockTickerService.getAll();
            console.log("Market tickers loaded:", marketTickersData);
            setMarketTickers(marketTickersData);
          } catch (err) {
            console.error("Error loading market tickers:", err);
            setMarketTickers([]);
          }
        })()
      ];

      // Wait for all parallel requests to complete
      await Promise.allSettled(loadPromises);

      console.log("All data loaded successfully");
    } catch (err) {
      console.error("Error in loadData:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadGmpData = async () => {
    try {
      console.log("Loading GMP data...");
      console.log("Current IPOs:", ipos.map(ipo => ({ id: ipo.id, company: ipo.company })));
      
      // Get GMP history for all IPOs
      const gmpPromises = ipos.map(async (ipo) => {
        try {
          console.log(`Fetching GMP for IPO ${ipo.id} (${ipo.company})`);
          const historyRows = await gmpService.getHistory(ipo.id);
          const normalizedHistory = mapGmpRowsToEntries(historyRows as GmpHistoryRow[]);
          console.log(`GMP history for ${ipo.id}:`, normalizedHistory);
          return { ipoId: ipo.id, history: normalizedHistory };
        } catch (err) {
          console.error(`Error loading GMP for IPO ${ipo.id}:`, err);
          return { ipoId: ipo.id, history: [] };
        }
      });

      const gmpResults = await Promise.all(gmpPromises);
      console.log("All GMP results:", gmpResults);
      
      // Create a map of IPO ID to GMP history
      const gmpHistoryMap: Record<string, GmpHistoryEntry[]> = {};
      gmpResults.forEach(({ ipoId, history }) => {
        gmpHistoryMap[ipoId] = history;
      });

      // Update IPOs with current GMP values
      const updatedIpos = ipos.map(ipo => {
        const history = gmpHistoryMap[ipo.id] || [];
        console.log(`Processing IPO ${ipo.company}:`, { history, historyLength: history.length });

        const snapshot = getGmpSnapshot(history);
        
        const updatedIpo = {
          ...ipo,
          gmp: snapshot.currentGmp,
          yesterdayGmp: snapshot.yesterdayGmp,
        };
        
        console.log(`Updated IPO ${ipo.company}:`, {
          latest: snapshot.currentGmp,
          yesterday: snapshot.yesterdayGmp,
          finalGmp: updatedIpo.gmp,
          finalYesterdayGmp: updatedIpo.yesterdayGmp
        });
        
        return updatedIpo;
      });

      setGmpHistory(gmpHistoryMap);
      setIpos(updatedIpos);
      console.log("GMP data loaded successfully");

    } catch (err) {
      console.error("Error loading GMP data:", err);
      // Set IPOs without GMP data if loading fails
      setIpos(ipos.map(ipo => ({
        ...ipo,
        gmp: 0,
        yesterdayGmp: 0,
      })));
    }
  };

  const checkAuthStatus = async () => {
    setAuthLoading(true);
    try {
      const current = await authService.getCurrentUser();
      const isAdminUser =
        current?.profile?.role === "admin" || current?.profile?.role === "super_admin";
      setIsAdmin(Boolean(isAdminUser));
    } catch (err) {
      console.log("Auth check failed:", err);
      setIsAdmin(false);
    } finally {
      setAuthLoading(false);
    }
  };

  // CRUD Operations
  const addIpo = async (ipo: IPO_Legacy) => {
    try {
      const newIpo = await ipoService.create(convertLegacyToIpo(ipo));
      const legacyIpo = convertIpoToLegacy(newIpo);
      const gmpValue = Number(ipo.gmp);
      const yesterdayGmpValue = Number(ipo.yesterdayGmp);

      // Keep newly created IPO GMP in sync with history table so list/detail views
      // immediately show the value entered in admin while creating IPO.
      if (Number.isFinite(gmpValue)) {
        const today = new Date().toISOString().split("T")[0];
        if (gmpValue !== 0) {
          await gmpService.create({
            ipo_id: newIpo.id,
            gmp_value: gmpValue,
            date: today,
            trend: null,
          });

          setGmpHistory((prev) => ({
            ...prev,
            [newIpo.id]: upsertGmpEntry(prev[newIpo.id] || [], { date: today, gmp: gmpValue }),
          }));
        }
      }

      if (Number.isFinite(gmpValue)) {
        legacyIpo.gmp = gmpValue;
      }

      if (Number.isFinite(yesterdayGmpValue)) {
        legacyIpo.yesterdayGmp = yesterdayGmpValue;
      }

      setIpos(prev => [...prev, legacyIpo]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add IPO");
      throw err;
    }
  };

  const updateIpo = async (id: string, ipoData: Partial<IPO_Legacy>) => {
    try {
      console.log("Converting IPO data for update:", ipoData);
      const convertedData = convertLegacyToIpo(ipoData as IPO_Legacy);
      console.log("Converted data for API:", convertedData);
      
      const result = await ipoService.update(id, convertedData);
      console.log("Update result:", result);
      
      // Auto-add GMP history entry if GMP is updated
      const currentIpo = ipos.find((ipo) => ipo.id === id);
      const nextGmp = Number(ipoData.gmp);
      const hasFiniteGmpUpdate = ipoData.gmp !== undefined && ipoData.gmp !== null && Number.isFinite(nextGmp);
      const gmpChanged = hasFiniteGmpUpdate && nextGmp !== Number(currentIpo?.gmp ?? 0);
      let updatedHistory = gmpHistory[id] || [];

      if (gmpChanged) {
        const today = new Date().toISOString().split("T")[0];
        console.log("Adding GMP entry for date:", today, "GMP:", nextGmp, "IPO ID:", id);
        await gmpService.create({ 
          ipo_id: id, 
          date: today, 
          gmp_value: nextGmp, 
          trend: 'stable' 
        });

        updatedHistory = upsertGmpEntry(updatedHistory, { date: today, gmp: nextGmp });
        setGmpHistory((prev) => ({
          ...prev,
          [id]: updatedHistory,
        }));
      }

      const nextIpoData: Partial<IPO_Legacy> = { ...ipoData };
      if (hasFiniteGmpUpdate) {
        const snapshot = getGmpSnapshot(updatedHistory);
        nextIpoData.gmp = nextGmp;
        nextIpoData.yesterdayGmp = snapshot.yesterdayGmp;
      }

      setIpos(prev => prev.map(i => i.id === id ? { ...i, ...nextIpoData } : i));
      console.log("IPO updated successfully in local state");
    } catch (err) {
      console.error("Error updating IPO:", err);
      setError(err instanceof Error ? err.message : "Failed to update IPO");
      throw err;
    }
  };

  const deleteIpo = async (id: string) => {
    try {
      await ipoService.delete(id);
      setIpos(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete IPO");
      throw err;
    }
  };

  const addNews = async (item: NewsItem_Legacy) => {
    try {
      const newNews = await newsService.create(convertLegacyToNews(item));
      const legacyNews = convertNewsToLegacy(newNews);
      setNews(prev => [...prev, legacyNews]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add news");
      throw err;
    }
  };

  const updateNews = async (id: string, newsData: Partial<NewsItem_Legacy>) => {
    try {
      const updatedNews = await newsService.update(id, convertLegacyToNews(newsData as NewsItem_Legacy));
      const legacyNews = convertNewsToLegacy(updatedNews);
      setNews((prev) => prev.map((n) => (n.id === id ? legacyNews : n)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update news");
      throw err;
    }
  };

  const deleteNews = async (id: string) => {
    try {
      await newsService.delete(id);
      setNews(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete news");
      throw err;
    }
  };

  const addJob = async (job: Job_Legacy) => {
    try {
      const newJob = await jobService.create(convertLegacyToJob(job));
      const legacyJob = convertJobToLegacy(newJob);
      setJobs(prev => [...prev, legacyJob]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add job");
      throw err;
    }
  };

  const updateJob = async (id: string, jobData: Partial<Job_Legacy>) => {
    try {
      await jobService.update(id, convertLegacyToJob(jobData as Job_Legacy));
      setJobs(prev => prev.map(j => j.id === id ? { ...j, ...jobData } : j));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update job");
      throw err;
    }
  };

  const deleteJob = async (id: string) => {
    try {
      await jobService.delete(id);
      setJobs(prev => prev.filter(j => j.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete job");
      throw err;
    }
  };

  const addLearning = async (learning: Learning_Legacy) => {
    try {
      const newLearning = await learningService.create(convertLegacyToLearning(learning));
      const legacyLearning = convertLearningToLegacy(newLearning);
      setLearning(prev => [...prev, legacyLearning]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add learning");
      throw err;
    }
  };

  const updateLearning = async (id: string, learning: Partial<Learning_Legacy>) => {
    try {
      const updatedLearning = await learningService.update(id, convertLegacyToLearning(learning as Learning_Legacy));
      setLearning(prev => prev.map(l => l.id === id ? convertLearningToLegacy(updatedLearning) : l));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update learning");
      throw err;
    }
  };

  const deleteLearning = async (id: string) => {
    try {
      await learningService.delete(id);
      setLearning(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete learning");
      throw err;
    }
  };

  const addAffiliate = async (link: AffiliateLink_Legacy) => {
    try {
      const newAffiliate = await affiliateService.create(convertLegacyToAffiliate(link));
      const legacyAffiliate = convertAffiliateToLegacy(newAffiliate);
      setAffiliates(prev => [...prev, legacyAffiliate]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add affiliate");
      throw err;
    }
  };

  const updateAffiliate = async (id: string, linkData: Partial<AffiliateLink_Legacy>) => {
    try {
      await affiliateService.update(id, convertLegacyToAffiliate(linkData as AffiliateLink_Legacy));
      setAffiliates(prev => prev.map(a => a.id === id ? { ...a, ...linkData } : a));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update affiliate");
      throw err;
    }
  };

  const deleteAffiliate = async (id: string) => {
    try {
      await affiliateService.delete(id);
      setAffiliates(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete affiliate");
      throw err;
    }
  };

  const addMarketTicker = async (ticker: MarketTicker_Legacy) => {
    try {
      const newTicker = await mockTickerService.add(convertLegacyToMarketTicker(ticker));
      const legacyTicker = convertMarketTickerToLegacy(newTicker);
      setMarketTickers(prev => [...prev, legacyTicker]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add market ticker");
      throw err;
    }
  };

  const updateMarketTicker = async (id: string, tickerData: Partial<MarketTicker_Legacy>) => {
    try {
      const updated = await mockTickerService.update(id, convertLegacyToMarketTicker(tickerData as MarketTicker_Legacy));
      const legacyTicker = convertMarketTickerToLegacy(updated);
      setMarketTickers(prev => prev.map(t => t.id === id ? legacyTicker : t));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update market ticker");
      throw err;
    }
  };

  const deleteMarketTicker = async (id: string) => {
    try {
      await mockTickerService.delete(id);
      setMarketTickers(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete market ticker");
      throw err;
    }
  };

  const addGmpEntry = async (ipoId: string, entry: GmpHistoryEntry) => {
    try {
      const gmpValue = normalizeGmpValue(entry.gmp);
      const normalizedEntry = { ...entry, gmp: gmpValue };

      await gmpService.create({
        ipo_id: ipoId,
        gmp_value: gmpValue,
        date: normalizedEntry.date,
        trend: null, // Can be calculated based on previous values
      });

      const nextHistory = upsertGmpEntry(gmpHistory[ipoId] || [], normalizedEntry);
      setGmpHistory(prev => {
        return {
          ...prev,
          [ipoId]: nextHistory,
        };
      });

      const snapshot = getGmpSnapshot(nextHistory);

      // Keep current/yesterday GMP in local IPO list synced from historical rows.
      setIpos((prev) =>
        prev.map((ipo) =>
          ipo.id === ipoId
            ? { ...ipo, gmp: snapshot.currentGmp, yesterdayGmp: snapshot.yesterdayGmp }
            : ipo
        )
      );

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add GMP entry");
      throw err;
    }
  };

  const adminLogin = async (email: string, password: string): Promise<boolean> => {
    try {
      const result = await authService.signIn(email, password);
      const isAdminUser = result.profile.role === "admin" || result.profile.role === "super_admin";
      setIsAdmin(isAdminUser);
      setAuthLoading(false);
      return isAdminUser;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      return false;
    }
  };

  const adminLogout = async (): Promise<void> => {
    try {
      await authService.signOut();
    } catch (err) {
      console.error("Logout error:", err);
    }

    setIsAdmin(false);
    setAuthLoading(false);
  };

  return (
    <DataContext.Provider
      value={{
        ipos, news, jobs, learning, affiliates, marketTickers, gmpHistory,
        newsCategories, jobCategories, jobLocations,
        addIpo, updateIpo, deleteIpo,
        addNews, updateNews, deleteNews,
        addJob, updateJob, deleteJob,
        addLearning, updateLearning, deleteLearning,
        addAffiliate, updateAffiliate, deleteAffiliate,
        addMarketTicker, updateMarketTicker, deleteMarketTicker,
        addGmpEntry,
        isAdmin, adminLogin, adminLogout,
        loading, authLoading, error
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
